import {useCallback, useEffect, useRef, useState} from "react";
import type {UploadProgress} from "./progressMath";
import {ProgressReadout, ProgressReadoutValues, READOUT_CADENCE_MS} from "./progressReadout";

/**
 * React adapter over {@link ProgressReadout} (ui#942) — it holds the model and the clock, and owns
 * no rule about what the readout says.
 *
 * The timer is not a detail: a progress event only arrives when bytes move, so without it a
 * transfer that goes quiet would keep showing the speed it had when the last bytes went, forever.
 * Ticking the model with the unchanged byte count is what lets it report the slowdown, and it is
 * also what makes the countdown advance through a stall.
 *
 * It ticks FASTER than the cadence on purpose. A timer at exactly the cadence beats against the
 * gate, which re-arms from the moment it actually published: one progress event landing 50 ms late
 * puts the two permanently out of phase and the readout skips a whole second. Offering the model
 * four chances per window leaves the gate as the only thing deciding when to publish.
 *
 * The clock is `performance.now`, not `Date.now`: a wall-clock correction mid-upload would otherwise
 * freeze the readout for as long as the jump.
 */
/** Four observations per cadence window — see the note above on phase. */
const TICK_MS = READOUT_CADENCE_MS / 4;

/**
 * One domain readout plus the WHOLE progress sample observed at that same publish boundary.
 *
 * The complete sample rather than two scalars, so every number on the card — the arc, the percentage,
 * the uploaded total AND the per-file rows — can move on one boundary. Feeding some of them the raw
 * stream instead is visible: on a single-file transfer the row reaches 100% while the gauge still
 * shows the previous second, and for up to a second after mount the gauge reads 0% beside rows that
 * are already moving.
 */
export interface ProgressReadoutSnapshot extends ProgressReadoutValues {
	readonly sample: UploadProgress;
}

/**
 * `active` is what lets an owner OUTLIVE one upload (ui#1839).
 *
 * The hook used to be called only from {@link ProgressCard}, which mounts for the progress phase alone,
 * so unmounting was both the "stop the timer" and the "forget the last transfer" mechanism. It moved to
 * `CreateFlow`, which owns the upload lifecycle and is mounted the whole time — so both jobs have to be
 * said out loud. Without the timer guard every landing-page visitor would run a 250 ms
 * interval forever; without the reset, transfer #2 would be measured against transfer #1's baseline and
 * open the model's first window with a rate that never happened.
 */
export function useProgressReadout(
	progress: UploadProgress,
	active = true,
): ProgressReadoutSnapshot | null {
	const modelRef = useRef<ProgressReadout | null>(null);
	const model = (modelRef.current ??= new ProgressReadout());
	const latest = useRef(progress);
	const published = useRef<ProgressReadoutValues | null>(null);
	const [readout, setReadout] = useState<ProgressReadoutSnapshot | null>(null);
	const capture = useCallback((values: ProgressReadoutValues | null, sample: UploadProgress) => {
		// Object identity is the domain gate's contract. Associating the counters here lets every gauge
		// value move on that SAME boundary without moving the cadence rule into React.
		if (values === null || values === published.current) {
			return;
		}
		published.current = values;
		setReadout({...values, sample});
	}, []);

	// Reset ONCE on the falling edge, not on every inactive render. Clearing `modelRef` makes the next
	// render build a new model, which changes this effect's own `model` dependency and runs it again —
	// so an unguarded reset clears the ref it has just refilled, forever.
	const observing = useRef(false);

	useEffect(() => {
		if (!active) {
			if (!observing.current) {
				return;
			}
			// A fresh model for the next transfer, and nothing left over on screen. `published` goes with
			// it: it is the identity the gate compares against, so keeping it would swallow the first
			// publish of the next upload.
			observing.current = false;
			modelRef.current = null;
			published.current = null;
			setReadout(null);
			return;
		}
		observing.current = true;
		latest.current = progress;
		capture(model.observe(progress.bytesSent, progress.totalBytes, performance.now()), progress);
	}, [active, capture, model, progress]);

	useEffect(() => {
		if (!active) {
			return;
		}
		const timer = setInterval(() => {
			const sample = latest.current;
			capture(model.observe(sample.bytesSent, sample.totalBytes, performance.now()), sample);
		}, TICK_MS);
		return () => clearInterval(timer);
	}, [active, capture, model]);

	return active ? readout : null;
}
